/**
 * Payroll Tax GraphQL Queries and Mutations
 *
 * Operations for managing tax tables via the ferp-payroll-integration
 * Supports syncing from data files to Wasabi S3 storage
 */

import { gql } from '@apollo/client';

// =============================================================================
// Types
// =============================================================================

export interface TaxSyncResult {
  federalSynced: boolean;
  federalVersion: string | null;
  statesSynced: number;
  pfmlSynced: number;
  sdiSynced: number;
  stateIncomeSynced: number;
  totalSynced: number;
  errors: string[];
  success: boolean;
}

export interface AvailableTaxYears {
  years: number[];
  source: string;
}

export interface TaxTable {
  id: string;
  year: number;
  jurisdiction: string;
  taxType: string;
  version: string;
  effectiveDate: string;
  expirationDate: string | null;
  rates: Record<string, unknown>;
  wageBase: number | null;
  employeeRate: number | null;
  employerRate: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaxTableSearchInput {
  year?: number;
  jurisdiction?: string;
  taxType?: string;
  limit?: number;
  offset?: number;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Get available tax years from data files
 */
export const AVAILABLE_TAX_YEARS = gql`
  query AvailableTaxYears {
    availableTaxYears {
      years
      source
    }
  }
`;

/**
 * Get tax tables with optional filtering
 */
export const GET_TAX_TABLES = gql`
  query GetTaxTables($input: TaxTableSearchInput!) {
    taxTables(input: $input) {
      id
      year
      jurisdiction
      taxType
      version
      effectiveDate
      expirationDate
      rates
      wageBase
      employeeRate
      employerRate
      createdAt
      updatedAt
    }
  }
`;

/**
 * Get a single tax table by ID
 */
export const GET_TAX_TABLE = gql`
  query GetTaxTable($id: ID!) {
    taxTable(id: $id) {
      id
      year
      jurisdiction
      taxType
      version
      effectiveDate
      expirationDate
      rates
      wageBase
      employeeRate
      employerRate
      createdAt
      updatedAt
    }
  }
`;

/**
 * Get federal tax rates for a specific year
 */
export const GET_FEDERAL_TAX_RATES = gql`
  query GetFederalTaxRates($year: Int!) {
    federalTaxRates(year: $year) {
      year
      socialSecurityRate
      socialSecurityWageBase
      medicareRate
      additionalMedicareRate
      additionalMedicareThreshold
      futaRate
      futaWageBase
      effectiveDate
      version
    }
  }
`;

/**
 * Get SUTA rates for a jurisdiction and year
 */
export const GET_SUTA_RATES = gql`
  query GetSutaRates($year: Int!, $jurisdiction: String!) {
    sutaRates(year: $year, jurisdiction: $jurisdiction) {
      year
      jurisdiction
      wageBase
      newEmployerRate
      minRate
      maxRate
      effectiveDate
      version
    }
  }
`;

/**
 * Get PFML rates for participating states
 */
export const GET_PFML_RATES = gql`
  query GetPfmlRates($year: Int!, $jurisdiction: String) {
    pfmlRates(year: $year, jurisdiction: $jurisdiction) {
      year
      jurisdiction
      programName
      wageBase
      employeeRate
      employerRate
      totalRate
      effectiveDate
      version
    }
  }
`;

/**
 * Get SDI rates for participating jurisdictions
 */
export const GET_SDI_RATES = gql`
  query GetSdiRates($year: Int!, $jurisdiction: String) {
    sdiRates(year: $year, jurisdiction: $jurisdiction) {
      year
      jurisdiction
      wageBase
      employeeRate
      employerRate
      effectiveDate
      version
    }
  }
`;

// =============================================================================
// Mutations
// =============================================================================

/**
 * Sync all tax data for a specific year from data files to Wasabi S3
 * Syncs: Federal + 51 SUTA + 10 PFML + 6 SDI + 51 State Income = ~119 tables
 */
export const SYNC_TAX_YEAR = gql`
  mutation SyncTaxYear($year: Int!) {
    syncTaxYear(year: $year) {
      federalSynced
      federalVersion
      statesSynced
      pfmlSynced
      sdiSynced
      stateIncomeSynced
      totalSynced
      errors
      success
    }
  }
`;

/**
 * Sync tax data for a single jurisdiction from data files to Wasabi S3
 */
export const SYNC_JURISDICTION = gql`
  mutation SyncJurisdiction($year: Int!, $jurisdiction: String!) {
    syncJurisdiction(year: $year, jurisdiction: $jurisdiction) {
      federalSynced
      federalVersion
      statesSynced
      pfmlSynced
      sdiSynced
      stateIncomeSynced
      totalSynced
      errors
      success
    }
  }
`;

/**
 * Force refresh a specific tax table from source
 */
export const REFRESH_TAX_TABLE = gql`
  mutation RefreshTaxTable($id: ID!) {
    refreshTaxTable(id: $id) {
      id
      version
      updatedAt
      success
    }
  }
`;

/**
 * Invalidate cached tax data for a year/jurisdiction
 */
export const INVALIDATE_TAX_CACHE = gql`
  mutation InvalidateTaxCache($year: Int!, $jurisdiction: String) {
    invalidateTaxCache(year: $year, jurisdiction: $jurisdiction) {
      success
      keysInvalidated
    }
  }
`;

// =============================================================================
// Subscriptions (for real-time sync status)
// =============================================================================

/**
 * Subscribe to tax sync progress
 */
export const TAX_SYNC_PROGRESS = gql`
  subscription TaxSyncProgress($syncId: ID!) {
    taxSyncProgress(syncId: $syncId) {
      syncId
      status
      progress
      currentStep
      totalSteps
      currentJurisdiction
      errors
      completedAt
    }
  }
`;

// =============================================================================
// Response Types
// =============================================================================

export interface AvailableTaxYearsResponse {
  availableTaxYears: AvailableTaxYears;
}

export interface GetTaxTablesResponse {
  taxTables: TaxTable[];
}

export interface GetTaxTableResponse {
  taxTable: TaxTable;
}

export interface SyncTaxYearResponse {
  syncTaxYear: TaxSyncResult;
}

export interface SyncJurisdictionResponse {
  syncJurisdiction: TaxSyncResult;
}

export interface TaxSyncProgressData {
  taxSyncProgress: {
    syncId: string;
    status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    progress: number;
    currentStep: number;
    totalSteps: number;
    currentJurisdiction: string | null;
    errors: string[];
    completedAt: string | null;
  };
}
